(function () {
  if (customElements.get('store-locator')) return;

  var COUNTRY_LABELS = { DE: 'Deutschland', AT: 'Österreich', CH: 'Schweiz' };

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function normalize(value) {
    return String(value || '')
      .toLowerCase()
      .trim()
      .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
      .replace(/\s+/g, ' ');
  }

  function distanceKm(a, b) {
    var toRad = Math.PI / 180;
    var dLat = (b.lat - a.lat) * toRad;
    var dLng = (b.lng - a.lng) * toRad;
    var h = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(a.lat * toRad) * Math.cos(b.lat * toRad) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return 6371 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  }

  function formatDistance(km) {
    return km < 10 ? km.toFixed(1).replace('.', ',') + ' km' : Math.round(km) + ' km';
  }

  function centroid(points) {
    var lat = 0, lng = 0;
    points.forEach(function (p) { lat += p.lat; lng += p.lng; });
    return { lat: lat / points.length, lng: lng / points.length };
  }

  class StoreLocator extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      var dataEl = this.querySelector('[data-stores]');
      var raw = [];
      try {
        raw = JSON.parse(dataEl ? dataEl.textContent : '[]');
      } catch (e) {
        console.error('store-locator: invalid store data', e);
      }

      // plus: 0 in Liquid turns invalid coordinates into 0 — drop those.
      this.stores = raw.filter(function (s) { return s.lat && s.lng; });
      this.maxResults = parseInt(this.dataset.maxResults, 10) || 10;
      this.origin = null;
      this.activeType = null;
      this.plzData = null;

      this.form = this.querySelector('[data-search-form]');
      this.input = this.querySelector('[data-search-input]');
      this.statusEl = this.querySelector('[data-status]');
      this.listEl = this.querySelector('[data-list]');
      this.filtersEl = this.querySelector('[data-filters]');
      this.geoButton = this.querySelector('[data-geolocate]');

      this.form.addEventListener('submit', this.onSubmit.bind(this));

      // "search" fires when the native clear (×) button empties the field.
      this.input.addEventListener('search', function () {
        if (!this.input.value) this.search('');
      }.bind(this));

      // Fetch the PLZ dataset (~280 KB gzip) once the visitor is about to search, not on page load.
      if (this.dataset.plzUrl) {
        this.input.addEventListener('focus', this.loadPlzData.bind(this), { once: true });
      }

      if (this.geoButton) {
        if ('geolocation' in navigator) {
          this.geoButton.addEventListener('click', this.useGeolocation.bind(this));
        } else {
          this.geoButton.hidden = true;
        }
      }

      this.listEl.addEventListener('click', function (event) {
        var button = event.target.closest('[data-show-on-map]');
        if (button) this.focusStore(button.dataset.showOnMap);
      }.bind(this));

      this.renderFilters();
      this.render();
      this.setupMap();

      // Deep link: /pages/...?plz=80331 or ?q=München
      var params = new URLSearchParams(window.location.search);
      var initial = params.get('plz') || params.get('q');
      if (initial) {
        this.input.value = initial;
        this.search(initial);
      }
    }

    onSubmit(event) {
      event.preventDefault();
      this.search(this.input.value);
    }

    async search(query) {
      query = String(query || '').trim();
      this.nameFilter = null;

      if (!query) {
        this.origin = null;
        this.render();
        return;
      }

      this.setStatus('Suche läuft …');

      // Exact chain name ("rewe") wins over place lookup — otherwise prefix matching could hit some town.
      var isChain = this.stores.some(function (s) { return normalize(s.chain) === normalize(query); });
      var origin = isChain ? null : await this.resolveQuery(query);

      if (origin) {
        origin.label = origin.label || query;
        this.setOrigin(origin);
      } else if (this.matchStoresByName(query).length) {
        // Not a place — but a chain or store name, e.g. "rewe".
        this.nameFilter = normalize(query);
        this.nameQuery = query;
        this.origin = null;
        this.render();
      } else {
        this.render(); // Drop a previous name filter so the list matches the message.
        this.setStatus('Wir konnten „' + query + '“ nicht finden. Versuch es mit einer anderen PLZ oder Stadt.');
        return;
      }

      var url = new URL(window.location.href);
      url.searchParams.set('q', query);
      url.searchParams.delete('plz');
      history.replaceState(null, '', url);
    }

    matchStoresByName(query) {
      var q = normalize(query);
      if (q.length < 3 || /^\d+$/.test(q)) return [];

      return this.stores.filter(function (s) {
        return normalize(s.chain) === q || normalize(s.name).indexOf(q) !== -1;
      });
    }

    async resolveQuery(query) {
      var fromDataset = await this.resolveFromPlzDataset(query);
      if (fromDataset) return fromDataset;

      // Fallback without dataset: match PLZ / city against the stores themselves.
      var q = normalize(query);
      var isZip = /^\d{4,5}$/.test(q);
      var matches = this.stores.filter(function (s) {
        return isZip ? s.zip === q : normalize(s.city).indexOf(q) === 0;
      });

      // Same PLZ region (first two digits) as a looser fallback. Same length only:
      // DE has 5 digits, AT/CH 4 — otherwise "80331" would match Zürich "8001".
      if (!matches.length && isZip) {
        matches = this.stores.filter(function (s) {
          return s.zip && s.zip.length === q.length && s.zip.slice(0, 2) === q.slice(0, 2);
        });
      }

      // Origin is a store position, not the user's — sort by it but don't show distances.
      return matches.length ? Object.assign(centroid(matches), { approximate: true }) : null;
    }

    loadPlzData() {
      if (!this._plzData) {
        // Format (scripts/store-locator/build-plz.js): { z: { DE: { "80331": [lat, lng] } }, p: [[country, municipality, lat, lng]] }
        this._plzData = fetch(this.dataset.plzUrl)
          .then(function (response) { return response.json(); })
          .catch(function (e) {
            console.error('store-locator: PLZ dataset failed to load', e);
            this._plzData = null;
            return null;
          }.bind(this));
      }

      return this._plzData;
    }

    async resolveFromPlzDataset(query) {
      if (!this.dataset.plzUrl) return null;

      var data = await this.loadPlzData();
      if (!data) return null;

      var q = normalize(query);
      var candidates = [];

      if (/^\d{4,5}$/.test(q)) {
        // 4-digit PLZ exist in both AT and CH (e.g. 1010 Wien / Lausanne).
        ['DE', 'AT', 'CH'].forEach(function (cc) {
          var hit = data.z[cc] && data.z[cc][q];
          if (hit) candidates.push({ lat: hit[0], lng: hit[1], label: q });
        });
      } else {
        candidates = data.p.filter(function (p) { return normalize(p[1]) === q; });

        // Partial input ("münch"): prefix matches, shortest names first.
        if (!candidates.length && q.length >= 3) {
          candidates = data.p
            .filter(function (p) { return normalize(p[1]).indexOf(q) === 0; })
            .sort(function (a, b) { return a[1].length - b[1].length; });
          candidates = candidates.filter(function (p) { return p[1].length === (candidates[0] || [])[1].length; });
        }

        candidates = candidates.map(function (p) { return { lat: p[2], lng: p[3], label: p[1] }; });
      }

      return this.pickCandidate(candidates);
    }

    // Ambiguous place names / PLZ: take the candidate closest to any store.
    pickCandidate(candidates) {
      if (candidates.length < 2 || !this.stores.length) return candidates[0] || null;

      var stores = this.stores;
      var nearest = function (c) {
        return Math.min.apply(null, stores.map(function (s) { return distanceKm(c, s); }));
      };

      return candidates.reduce(function (best, c) { return nearest(c) < nearest(best) ? c : best; });
    }

    useGeolocation() {
      this.setStatus('Standort wird ermittelt …');

      navigator.geolocation.getCurrentPosition(
        function (position) {
          this.input.value = '';
          this.setOrigin({ lat: position.coords.latitude, lng: position.coords.longitude, label: 'deinem Standort' });
        }.bind(this),
        function () {
          this.setStatus('Dein Standort konnte nicht ermittelt werden. Gib stattdessen deine PLZ oder Stadt ein.');
        }.bind(this),
        { timeout: 10000, maximumAge: 300000 }
      );
    }

    setOrigin(origin) {
      this.origin = origin;
      this.stores.forEach(function (s) { s.distance = distanceKm(origin, s); });
      this.render();
      this.dispatchEvent(new CustomEvent('store-locator:origin', { detail: origin, bubbles: true }));
    }

    setStatus(text) {
      this.statusEl.textContent = text;
    }

    renderFilters() {
      if (!this.filtersEl) return;

      var types = [];
      this.stores.forEach(function (s) {
        (s.types || []).forEach(function (t) { if (types.indexOf(t) === -1) types.push(t); });
      });

      // A single type is not worth filtering on.
      if (types.length < 2) return;

      this.filtersEl.innerHTML = ['Alle'].concat(types).map(function (type, index) {
        return '<button type="button" class="store-locator__chip" aria-pressed="' + (index === 0) + '" data-type="' +
          (index === 0 ? '' : escapeHtml(type)) + '">' + escapeHtml(type) + '</button>';
      }).join('');
      this.filtersEl.hidden = false;

      this.filtersEl.addEventListener('click', function (event) {
        var chip = event.target.closest('[data-type]');
        if (!chip) return;

        this.activeType = chip.dataset.type || null;
        this.filtersEl.querySelectorAll('[data-type]').forEach(function (el) {
          el.setAttribute('aria-pressed', String(el === chip));
        });
        this.render();
      }.bind(this));
    }

    getVisibleStores() {
      var activeType = this.activeType;
      var byName = this.nameFilter ? this.matchStoresByName(this.nameFilter) : null;
      var stores = this.stores.filter(function (s) {
        return (!activeType || (s.types || []).indexOf(activeType) !== -1) && (!byName || byName.indexOf(s) !== -1);
      });

      if (this.origin) {
        stores.sort(function (a, b) { return a.distance - b.distance; });
        return stores.slice(0, this.maxResults);
      }

      return stores.sort(function (a, b) {
        return (a.country + a.city + a.name).localeCompare(b.country + b.city + b.name, 'de');
      });
    }

    render() {
      var stores = this.getVisibleStores();

      if (!this.stores.length) {
        this.setStatus('Bald findest du Vetain hier in Märkten in deiner Nähe.');
      } else if (this.nameFilter) {
        this.setStatus(stores.length + (stores.length === 1 ? ' Markt' : ' Märkte') + ' für „' + this.nameQuery + '“ – gib deine PLZ ein, um die Entfernung zu sehen.');
      } else if (this.origin) {
        this.setStatus(stores.length === 1
          ? 'Der nächstgelegene Markt zu ' + this.origin.label + ':'
          : 'Die ' + stores.length + ' nächstgelegenen Märkte zu ' + this.origin.label + ':');
      } else {
        this.setStatus(this.stores.length + (this.stores.length === 1 ? ' Markt' : ' Märkte') + ' mit Vetain');
      }

      this.listEl.innerHTML = stores.map(this.renderStore.bind(this)).join('');
      this.updateMap(stores);
    }

    setupMap() {
      this.mapEl = this.querySelector('[data-map]');
      this.mapConsentEl = this.querySelector('[data-map-consent]');
      if (!this.mapEl) return;

      this.querySelector('[data-map-load]').addEventListener('click', this.loadMap.bind(this));

      // Visitors who already accepted Cookiebot "preferences" skip the click-to-load step.
      var hasConsent = function () {
        return window.Cookiebot && window.Cookiebot.consent && window.Cookiebot.consent.preferences;
      };

      if (hasConsent()) {
        this.loadMap();
      } else {
        window.addEventListener('CookiebotOnAccept', function () {
          if (hasConsent()) this.loadMap();
        }.bind(this));
      }
    }

    loadMap() {
      if (this._mapLoading) return this._mapLoading;

      var loadCss = new Promise(function (resolve) {
        var link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = this.dataset.leafletCss;
        link.onload = link.onerror = resolve;
        document.head.appendChild(link);
      }.bind(this));

      var loadJs = window.L ? Promise.resolve() : new Promise(function (resolve, reject) {
        var script = document.createElement('script');
        script.src = this.dataset.leafletJs;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      }.bind(this));

      this._mapLoading = Promise.all([loadCss, loadJs])
        .then(this.initMap.bind(this))
        .catch(function (e) {
          this._mapLoading = null;
          console.error('store-locator: map failed to load', e);
        }.bind(this));

      return this._mapLoading;
    }

    initMap() {
      var L = window.L;

      this.mapEl.hidden = false;
      if (this.mapConsentEl) this.mapConsentEl.hidden = true;

      // Scroll-zoom only after the map is clicked, so the page scrolls normally past it.
      // No Leaflet animations: they wait for requestAnimationFrame / transitionend, which stall in the
      // theme editor iframe and background tabs — leaving tiles at opacity 0, mixed zoom levels and hidden pins.
      this.map = L.map(this.mapEl, {
        scrollWheelZoom: false,
        fadeAnimation: false,
        zoomAnimation: false,
        markerZoomAnimation: false
      });
      this.map.once('click focus', function () { this.map.scrollWheelZoom.enable(); }.bind(this));

      L.tileLayer(this.dataset.tileUrl, {
        maxZoom: 19,
        attribution: this.dataset.tileAttribution
      }).addTo(this.map);

      var pin = L.divIcon({ className: 'store-locator__pin', iconSize: [22, 22] });

      this.markers = {};
      this.stores.forEach(function (store) {
        this.markers[store.id] = L.marker([store.lat, store.lng], { icon: pin, title: store.name })
          .bindPopup(this.renderPopup(store));
      }.bind(this));

      // Layout around the map can still shift (fonts, list, sticky header) — keep Leaflet's size in sync.
      if ('ResizeObserver' in window) {
        new ResizeObserver(function () { this.map.invalidateSize(); }.bind(this)).observe(this.mapEl);
      }

      this.map.setView([48.3, 11.5], 5); // DACH overview until updateMap() fits the stores.
      this.render(); // Re-render: adds "Auf Karte zeigen" links and places markers.
    }

    updateMap(stores) {
      if (!this.map) return;

      var L = window.L;
      var visible = {};
      stores.forEach(function (s) { visible[s.id] = true; });

      Object.keys(this.markers).forEach(function (id) {
        if (visible[id]) {
          this.markers[id].addTo(this.map);
        } else {
          this.markers[id].remove();
        }
      }.bind(this));

      if (this.originMarker) this.originMarker.remove();

      var points = stores.map(function (s) { return [s.lat, s.lng]; });

      if (this.origin) {
        if (!this.origin.approximate) {
          this.originMarker = L.marker([this.origin.lat, this.origin.lng], {
            icon: L.divIcon({ className: 'store-locator__pin store-locator__pin--origin', iconSize: [16, 16] }),
            title: this.origin.label,
            zIndexOffset: -100
          }).addTo(this.map);
        }

        // Frame the search origin plus the three nearest stores.
        points = [[this.origin.lat, this.origin.lng]].concat(points.slice(0, 3));
      }

      if (points.length === 1) {
        this.map.setView(points[0], 13);
      } else if (points.length) {
        this.map.fitBounds(points, { padding: [40, 40], maxZoom: 13 });
      }
    }

    focusStore(id) {
      var marker = this.markers && this.markers[id];
      if (!marker) return;

      this.map.setView(marker.getLatLng(), Math.max(this.map.getZoom(), 14));
      marker.openPopup();

      if (window.matchMedia('(max-width: 999px)').matches) {
        this.mapEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    renderPopup(store) {
      return '<div class="store-locator__popup">' +
        '<p><strong>' + escapeHtml(store.name) + '</strong></p>' +
        '<p>' + escapeHtml(store.street + ', ' + store.zip + ' ' + store.city) + '</p>' +
        ((store.types || []).length ? '<p>' + escapeHtml(store.types.join(' · ')) + '</p>' : '') +
        (store.url ? '<a href="' + escapeHtml(store.url) + '">Produkte &amp; Infos</a>' : '') +
      '</div>';
    }

    renderStore(store) {
      var logo = store.logo
        ? '<img class="store-locator__logo" src="' + escapeHtml(store.logo) + '" alt="' + escapeHtml(store.chain) + '" loading="lazy" width="56" height="56">'
        : '<span class="store-locator__logo store-locator__logo--fallback" aria-hidden="true">' + escapeHtml(store.chain) + '</span>';

      var address = store.street + ', ' + store.zip + ' ' + store.city;
      var routeUrl = 'https://www.google.com/maps/dir/?api=1&destination=' +
        encodeURIComponent((store.chain ? store.chain + ' ' : '') + address);

      var types = (store.types || []).map(function (t) {
        return '<span class="store-locator__type">' + escapeHtml(t) + '</span>';
      }).join('');

      var distance = this.origin && !this.origin.approximate && typeof store.distance === 'number'
        ? '<span class="store-locator__distance" title="Luftlinie">ca. ' + formatDistance(store.distance) + '</span>'
        : '';

      var moreLink = store.url
        ? '<a class="link" href="' + escapeHtml(store.url) + '">Produkte &amp; Infos</a>'
        : '';

      return '<li class="store-locator__item">' +
        logo +
        '<div>' +
          '<p class="store-locator__name heading h6">' + escapeHtml(store.name) + '</p>' +
          '<p>' + escapeHtml(address) + (store.country && store.country !== 'DE' ? ', ' + escapeHtml(COUNTRY_LABELS[store.country] || store.country) : '') + '</p>' +
          (types ? '<div class="store-locator__types">' + types + '</div>' : '') +
          '<div class="store-locator__links">' +
            (this.map ? '<button type="button" class="store-locator__map-link" data-show-on-map="' + escapeHtml(store.id) + '">Auf Karte zeigen</button>' : '') +
            moreLink +
            '<a class="link" href="' + escapeHtml(routeUrl) + '" target="_blank" rel="noopener">Route planen</a>' +
          '</div>' +
        '</div>' +
        distance +
      '</li>';
    }
  }

  customElements.define('store-locator', StoreLocator);
})();
